import type { Injector } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { OnyxPhonePipe, OnyxPreferencesService } from '@onyx/angular';
import type { z } from 'zod';
import { DictionaryCode } from '../../../../../common/enums/dictionary-code';
import type { Contractor, ShippingType } from '../interfaces/contractor';
import { ExportHelper } from './../../../../common/helpers/export.helper';
import type { getContractorsXlsxSchema } from './contractors-xlsx-schema';

export type ContractorExportData = z.input<
  ReturnType<typeof getContractorsXlsxSchema>
>;

export const getContractorExportData = (
  contractor: Contractor,
  injector: Injector,
): ContractorExportData => {
  const translateService = injector.get(TranslateService);
  const phonePipe = injector.get(OnyxPhonePipe);
  const preferencesService = injector.get(OnyxPreferencesService);

  const {
    companyProfile,
    addresses,
    payment,
    invoiceDocuments,
    invoices,
    note,
  } = contractor;

  const mapShippingType = (shippingType: ShippingType) => {
    return {
      emails: shippingType.emails?.length
        ? shippingType.emails.join(', ')
        : null,
      internalPortalUrl: shippingType.internalPortal?.url ?? null,
      internalPortalLogin: shippingType.internalPortal?.login ?? null,
      internalPortalPassword: shippingType.internalPortal?.password ?? null,
      traditionalMail: shippingType.traditionalMail ?? false,
      isOriginalDocumentRequired:
        shippingType.isOriginalDocumentRequired ?? false,
    };
  };

  const invoice = invoices[0];
  const invoiceData = mapShippingType(invoice.shippingType);
  const invoiceDocumentsData = mapShippingType(
    invoiceDocuments[0].shippingType,
  );

  return {
    country: ExportHelper.mapField(
      companyProfile.country,
      DictionaryCode.COUNTRY,
    ),
    vatId: companyProfile.vatId,
    displayName: companyProfile.displayName,
    companyName: companyProfile.companyName,
    types: companyProfile.types
      ? ExportHelper.mapField(
          companyProfile.types.map(({ value }) => value),
          DictionaryCode.CONTRACTOR_TYPE,
          translateService,
        )
      : null,
    phone: companyProfile.phone
      ? phonePipe.transform(companyProfile.phone)
      : null,

    /* addresses */
    mainAddress: addresses.mainAddress.label,
    correspondenceAddress: addresses?.correspondenceAddress?.label ?? null,

    /* payment */
    termDays: payment.termDays,
    termType: ExportHelper.mapField(
      payment.termType,
      DictionaryCode.PAYMENT_TERM_TYPE,
    ),
    selectedTermDay: payment.selectedTermDay,
    tradeCreditLimitValue: payment.parameters.tradeCreditLimit?.value ?? null,
    tradeCreditLimitCurrency: ExportHelper.mapField(
      payment.parameters.tradeCreditLimit?.currency ??
        preferencesService.defaultCurrency(),
      DictionaryCode.CURRENCY,
    ),
    cashDiscountPercentage:
      payment.parameters.cashDiscount?.[0]?.percentage ?? null,
    cashDiscountTermDays:
      payment.parameters.cashDiscount?.[0]?.termDays ?? null,
    hasFactoringConsent: payment.parameters.hasFactoringConsent,

    /* invoiceDocuments */
    invoiceDocumentsEmails: invoiceDocumentsData.emails,
    invoiceDocumentsInternalPortalUrl: invoiceDocumentsData.internalPortalUrl,
    invoiceDocumentsInternalPortalLogin:
      invoiceDocumentsData.internalPortalLogin,
    invoiceDocumentsInternalPortalPassword:
      invoiceDocumentsData.internalPortalPassword,
    invoiceDocumentsTraditionalMail: invoiceDocumentsData.traditionalMail,
    invoiceDocumentsIsOriginalDocumentRequired:
      invoiceDocumentsData.isOriginalDocumentRequired,

    /* invoices */
    invoicesDefaultType: ExportHelper.mapField(
      invoice.defaultType,
      DictionaryCode.INVOICE_TYPE,
    ),
    invoicesCollectiveInvoiceIssueTerm: ExportHelper.mapField(
      invoice.collectiveInvoiceIssueTerm,
      DictionaryCode.COLLECTIVE_INVOICE_ISSUE_TERM,
    ),
    invoicesCollectiveInvoiceIssueSelectedDay:
      invoice.collectiveInvoiceIssueSelectedDay,

    invoicesEmails: invoiceData.emails,
    invoicesInternalPortalUrl: invoiceData.internalPortalUrl,
    invoicesInternalPortalLogin: invoiceData.internalPortalLogin,
    invoicesInternalPortalPassword: invoiceData.internalPortalPassword,
    invoicesTraditionalMail: invoiceData.traditionalMail,
    invoicesIsOriginalDocumentRequired: invoiceData.isOriginalDocumentRequired,

    invoicesPrimaryCurrency: ExportHelper.mapField(
      invoice.primaryCurrency,
      DictionaryCode.CURRENCY,
    ),
    invoicesPrimaryLanguage: ExportHelper.mapField(
      invoice.primaryLanguage,
      DictionaryCode.LANGUAGE,
    ),

    /* pointsOfInterest */
    pointsOfInterest: contractor.pointsOfInterest
      .map((item) => item.pointOfInterest.uuid)
      .join(', '),

    /* note */
    note: note?.content ?? null,
  } satisfies ContractorExportData;
};
